import React, { useEffect, useRef, useState } from "react";
import { Download, ExternalLink, Loader2, Minus, Plus, X } from "lucide-react";
import Printer from "lucide-react/dist/esm/icons/printer";
import PenTool from "lucide-react/dist/esm/icons/pen-tool";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError } from "../lib/errors";
import { useTopmostEscape } from "./UI";

const ZOOMS = [50, 75, 100, 125, 150, 200, 300];

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "document.pdf";
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * LT-39 — view-only PDF popup. Fetches the file through the authenticated API,
 * shows it in the browser's PDF viewer (blob URL) with zoom / print / download /
 * open-in-new-tab. `src` is an API path (e.g. /tenders/{id}/drawings/{did}/file).
 */
export function PdfViewerModal({ open, onClose, src, title, subtitle, filename, onBluebeam, testid = "pdf-viewer" }) {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!open || !src) return undefined;
    let alive = true;
    let objectUrl = null;
    setBlob(null); setUrl(null); setError(null); setZoom(100);
    api.get(src, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        const b = new Blob([r.data], { type: "application/pdf" });
        objectUrl = window.URL.createObjectURL(b);
        setBlob(b); setUrl(objectUrl);
      })
      .catch((e) => { if (alive) setError(formatApiError(e, "Could not load the PDF.")); });
    return () => { alive = false; if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [open, src]);

  useTopmostEscape(open, onClose);

  if (!open) return null;
  const zi = ZOOMS.indexOf(zoom);
  const step = (dir) => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, zi + dir))]);
  const print = () => {
    try {
      const w = frameRef.current?.contentWindow;
      if (w) { w.focus(); w.print(); return; }
    } catch (_) { /* cross-viewer quirks — fall through */ }
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("The PDF has not finished loading yet.");
  };

  const btn = "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-line bg-surface text-ink-2 hover:text-ink hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }} data-testid={`${testid}-backdrop`}>
      <div className="w-full h-full max-w-[1400px] bg-surface border border-line rounded-lg shadow-card overflow-hidden flex flex-col" data-testid={testid}>
        <div className="flex items-center justify-between gap-4 px-4 py-2.5 border-b border-line">
          <div className="min-w-0">
            <div className="text-ink truncate" style={{ fontSize: 14, fontWeight: 600 }} data-testid={`${testid}-title`}>{title || filename || "PDF"}</div>
            {subtitle && <div className="text-ink-3 truncate" style={{ fontSize: 11.5 }}>{subtitle}</div>}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0" style={{ fontSize: 12 }}>
            <button type="button" className={btn} onClick={() => step(-1)} disabled={!url || zi <= 0} aria-label="Zoom out" data-testid={`${testid}-zoom-out`}><Minus size={13} /></button>
            <span className="font-mono tnum text-ink-2 w-12 text-center" data-testid={`${testid}-zoom`}>{zoom}%</span>
            <button type="button" className={btn} onClick={() => step(1)} disabled={!url || zi >= ZOOMS.length - 1} aria-label="Zoom in" data-testid={`${testid}-zoom-in`}><Plus size={13} /></button>
            <span className="w-px h-5 bg-line mx-1" />
            <button type="button" className={btn} onClick={print} disabled={!url} data-testid={`${testid}-print`}><Printer size={13} /> Print</button>
            <button type="button" className={btn} onClick={() => blob && saveBlob(blob, filename)} disabled={!blob} data-testid={`${testid}-download`}><Download size={13} /> Download</button>
            <button type="button" className={btn} onClick={() => url && window.open(url, "_blank", "noopener")} disabled={!url} title="Open in a new browser tab" data-testid={`${testid}-open-tab`}><ExternalLink size={13} /> New tab</button>
            {onBluebeam && <button type="button" className={btn} onClick={onBluebeam} title="Open this drawing directly in Bluebeam Revu" data-testid={`${testid}-bluebeam`}><PenTool size={13} /> Bluebeam</button>}
            <button type="button" onClick={onClose} className="ml-1 h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-3 hover:text-ink hover:bg-surface-2" aria-label="Close" data-testid={`${testid}-close`}><X size={15} /></button>
          </div>
        </div>
        <div className="flex-1 bg-surface-2 relative min-h-0">
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center text-note-red" style={{ fontSize: 13 }} data-testid={`${testid}-error`}>{error}</div>
          ) : !url ? (
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-ink-3" style={{ fontSize: 13 }} data-testid={`${testid}-loading`}><Loader2 size={14} className="animate-spin" /> Loading PDF…</div>
          ) : (
            <iframe ref={frameRef} key={`${url}#${zoom}`} src={`${url}#zoom=${zoom}`} title={title || filename || "PDF"} className="w-full h-full border-0" data-testid={`${testid}-frame`} />
          )}
        </div>
        <div className="px-4 py-1.5 border-t border-line text-ink-4 flex items-center justify-between gap-4" style={{ fontSize: 10.5 }}>
          <span>View only — the stored file is never changed here. {filename ? <span className="font-mono">{filename}</span> : null}</span>
          <span data-testid={`${testid}-revu-hint`}>{onBluebeam ? "Click Bluebeam to open this drawing straight in Revu (one-time PC setup), or Download and open it in your PDF app." : "To mark up in Bluebeam Revu: Download, then open the file in Revu (or set Revu as your default PDF app)."}</span>
        </div>
      </div>
    </div>
  );
}
